import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { FileSpreadsheet, Download, Loader2 } from 'lucide-react';

const MONTHS_BACK = 18;
const QUARTERS = [
  { k: 'Q1', l: 'Q1 · Apr–Jun' },
  { k: 'Q2', l: 'Q2 · Jul–Sep' },
  { k: 'Q3', l: 'Q3 · Oct–Dec' },
  { k: 'Q4', l: 'Q4 · Jan–Mar' },
];

const fyLabel = (d) => {
  const y = d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-${String(y + 1).slice(2)}`;
};
const currentQuarter = (d) => {
  const m = d.getMonth() + 1;
  return m <= 3 ? 'Q4' : m <= 6 ? 'Q1' : m <= 9 ? 'Q2' : 'Q3';
};

/**
 * One-click GST + TDS pack for the CA — a 3-sheet .xlsx (Invoices ·
 * TDS by PAN · Client Receipts) for a calendar month or an FY quarter.
 */
export default function CaPackDownload() {
  const { api } = useAuth();
  const now = new Date();
  const [mode, setMode] = useState('month');
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [fy, setFy] = useState(fyLabel(now));
  const [quarter, setQuarter] = useState(currentQuarter(now));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const fyOptions = Array.from({ length: 3 }, (_, i) => {
    const y = Number(fyLabel(now).split('-')[0]) - i;
    return `${y}-${String(y + 1).slice(2)}`;
  });
  const monthOptions = Array.from({ length: MONTHS_BACK }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return {
      k: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      l: d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }),
    };
  });

  const download = async () => {
    setBusy(true); setMsg('');
    const p = new URLSearchParams();
    if (mode === 'month') { p.set('period', 'month'); p.set('month', month); }
    else { p.set('period', 'quarter'); p.set('fy', fy); p.set('quarter', quarter); }
    try {
      const r = await api.get(`/accounting/ca-pack?${p.toString()}`, { responseType: 'blob' });
      const name = mode === 'month' ? month : `FY${fy}_${quarter}`;
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `VOS_CA_Pack_${name}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setMsg('Downloaded');
      setTimeout(() => setMsg(''), 4000);
    } catch (e) {
      setMsg('Could not build the pack');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="ca-pack-download">
      <div className="flex items-center gap-1.5 mr-1">
        <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
        <span className="text-[10px] uppercase tracking-wider text-zinc-400">CA Pack · GST + TDS</span>
      </div>
      <div className="flex bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden">
        <button onClick={() => setMode('month')} data-testid="ca-pack-mode-month"
          className={`text-[10px] px-2 py-1 transition-colors ${mode === 'month' ? 'bg-emerald-500 text-zinc-950 font-bold' : 'text-zinc-400 hover:text-white'}`}>Month</button>
        <button onClick={() => setMode('quarter')} data-testid="ca-pack-mode-quarter"
          className={`text-[10px] px-2 py-1 transition-colors ${mode === 'quarter' ? 'bg-emerald-500 text-zinc-950 font-bold' : 'text-zinc-400 hover:text-white'}`}>Quarter</button>
      </div>

      {mode === 'month' ? (
        <select value={month} onChange={e => setMonth(e.target.value)} data-testid="ca-pack-month-select"
          className="bg-zinc-950 border border-zinc-800 rounded-sm text-[10px] text-zinc-300 px-2 py-1 outline-none">
          {monthOptions.map(m => <option key={m.k} value={m.k}>{m.l}</option>)}
        </select>
      ) : (
        <>
          <select value={fy} onChange={e => setFy(e.target.value)} data-testid="ca-pack-fy-select"
            className="bg-zinc-950 border border-zinc-800 rounded-sm text-[10px] text-zinc-300 px-2 py-1 outline-none">
            {fyOptions.map(f => <option key={f} value={f}>FY {f}</option>)}
          </select>
          <select value={quarter} onChange={e => setQuarter(e.target.value)} data-testid="ca-pack-quarter-select"
            className="bg-zinc-950 border border-zinc-800 rounded-sm text-[10px] text-zinc-300 px-2 py-1 outline-none">
            {QUARTERS.map(q => <option key={q.k} value={q.k}>{q.l}</option>)}
          </select>
        </>
      )}

      <button onClick={download} disabled={busy} data-testid="ca-pack-download-btn"
        className="h-7 px-3 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-zinc-950 text-[10px] font-bold rounded-sm flex items-center gap-1.5">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
        {busy ? 'Building…' : 'Download .xlsx'}
      </button>
      {msg && <span className="text-[10px] text-zinc-400" data-testid="ca-pack-msg">{msg}</span>}
    </div>
  );
}
